import { ImagePaths } from '../../lib/imageUrls.js';

export const microsoftActiveDirectoryFundamentals = {

    id: 2,
    slug: 'microsoft-active-directory-fundamentals',
    ogImage: '/og/courses/microsoft-active-directory-fundamentals.jpg',
    courseCode: 'AD-FND-001',
    title: 'Microsoft Active Directory Fundamentals',
    subtitle: 'Master Microsoft Active Directory essentials',
    valueProposition: 'Build foundational skills in Microsoft Active Directory to support enterprise identity and access management across Australian organisations.',
    duration: '8 hours',
    students: 'New',
    level: 'Foundational',
    organization: 'Microsoft',
    image: ImagePaths.COURSE_1,
    shortDescription: 'AD-FND-001 — Active Directory fundamentals covering domains, Group Policy and user management for career changers entering IT administration.',
    price: {
      aud: 499,
      currency: 'AUD',
      earlyBird: 399
    },
    stripeConfig: {
    priceId: 'price_1U3WMlKu1nyc322U98bxSuRX',
    productId: 'prod_V3deXVoBqTZd1g'
  },
    
    snapshot: {
      whoItsFor: [
        'Career changers entering IT administration',
        'Return to workforce professionals',
        'University students preparing for IT careers',
        'IT support staff expanding their skills'
      ],
      accreditation: 'Foundation for Microsoft 365 and enterprise identity management'
    },
    
    logistics: {
      classSize: 'Maximum 20 participants for interactive engagement',
      requirements: [
        'Computer with Windows 10+ or macOS',
        'Reliable internet connection (minimum 5 Mbps)',
        'No prior experience required'
      ]
    },
    
    overview: 'Get started with Microsoft Active Directory, the foundation of identity and access management in modern organisations. This introductory course covers the essential concepts, components, and practices of Active Directory to support organisational security and compliance. Perfect for career changers, returners to the workforce, and students beginning their IT careers.',
    
    outcomes: [
      'Understand Active Directory architecture and fundamental concepts',
      'Explain domain controllers, forests, and trust relationships',
      'Create and manage user and computer accounts in Active Directory',
      'Implement group policies for security and compliance',
      'Apply user and group permissions across organisational resources',
      'Troubleshoot common Active Directory connectivity and authentication issues',
      'Support enterprise identity governance and compliance requirements',
      'Prepare for advanced Active Directory administration and Microsoft certifications'
    ],
    
    audience: [
      'Career changers entering IT administration',
      'Return to workforce professionals',
      'University students entering IT careers',
      'IT support and help desk staff',
      'Administrators new to Microsoft environments'
    ],
    
    prerequisites: [
      'Basic computer and networking knowledge',
      'Familiarity with Windows operating system',
      'No prior Active Directory experience required'
    ],
    
    modules: [
      {
        title: 'Active Directory Fundamentals',
        duration: '2 hours',
        topics: [
          'Active Directory overview and purpose',
          'Domain, forest, and trust architecture',
          'Organisational units and domains',
          'DNS integration and name resolution'
        ]
      },
      {
        title: 'User and Computer Management',
        duration: '2 hours',
        topics: [
          'Creating and managing user accounts',
          'Computer account management',
          'User properties and profiles',
          'Group membership and security groups'
        ]
      },
      {
        title: 'Group Policy and Security',
        duration: '2 hours',
        topics: [
          'Group Policy Object (GPO) fundamentals',
          'Applying security policies to users and computers',
          'Password policies and account lockout',
          'User rights and permissions'
        ]
      },
      {
        title: 'Permissions and Access Control',
        duration: '1.5 hours',
        topics: [
          'NTFS and share permissions',
          'Implementing least privilege access',
          'Delegation of administrative tasks',
          'Compliance and security best practices'
        ]
      },
      {
        title: 'Troubleshooting and Support',
        duration: '0.5 hours',
        topics: [
          'Diagnosing authentication issues',
          'Common problems and solutions',
          'Support resources and tools',
          'Next steps in your AD learning journey'
        ]
      }
    ],
    
    format: {
      type: 'Live online training',
      includes: [
        '8 hours of interactive instruction',
        'Hands-on demonstrations in Active Directory',
        'Course materials and reference guides',
        'Q&A sessions with instructor',
        'Certificate of completion',
        '3 months access to recorded sessions'
      ]
    },
    
    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'New sessions available most Mondays and Thursdays'
    },
    
    faqs: [
      {
        question: 'Is this course suitable for complete beginners?',
        answer: 'Absolutely! This course is designed for career changers, returners to the workforce, and students with no prior Active Directory experience. We start with fundamentals and build your knowledge step by step.'
      },
      {
        question: 'Do I need to set up Active Directory on my own computer?',
        answer: 'No, we provide a shared lab environment for all hands-on activities. You just need a web browser and internet connection to access the labs. This makes it accessible regardless of your home setup.'
      },
      {
        question: 'Will this prepare me for Microsoft certifications?',
        answer: 'This course provides essential foundational knowledge that supports Microsoft certifications like AZ-104 (Azure Administrator) and AZ-305 (Azure Solutions Architect). It\'s an excellent starting point before pursuing more advanced certifications.'
      },
      {
        question: 'How much time should I allocate for learning?',
        answer: 'The course is 8 hours of live instruction, typically completed over one or two days. We recommend allowing additional time for reviewing materials and practising concepts. Most participants can engage alongside their existing work or studies.'
      },
      {
        question: 'What support is available during and after the course?',
        answer: 'During the course, you\'ll have direct access to the instructor for questions. After completion, you\'ll have access to recorded sessions for 3 months and can contact our support team for clarification on course topics.'
      },
      {
        question: 'Do you offer group rates for teams?',
        answer: 'Yes, we offer discounted rates for groups of 5 or more participants. If you\'re planning group training for your organisation or educational institution, contact us for custom pricing and scheduling options.'
      },
      {
        question: 'Is this course available for in-house delivery?',
        answer: 'Yes, we can deliver customised in-house training tailored to your organisation\'s specific Active Directory setup and requirements. Contact us to discuss your organisation\'s needs and receive a quote.'
      },
      {
        question: 'What payment methods do you accept?',
        answer: 'We accept all major credit cards (Visa, Mastercard, American Express) processed securely through Stripe. For group bookings or organisations, we can accommodate invoicing and purchase orders. All prices are in Australian dollars (AUD) and include GST.'
      }
    ]
};
